/**
 * « Créer » : la même liste, filtrée par droits, en feuille au pouce sur
 * téléphone (bouton ＋ de la barre du bas) et en menu sous le bouton de la
 * colonne sur ordinateur. Chaque entrée ouvre sa page de saisie et lui donne
 * l'écran d'origine comme retour. Le geste principal suit la vue : scanner un QR
 * code au terrain, la création la plus fréquente au bureau (`createLead`).
 */
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { t } from '../../core/i18n.js';
import { ICONS } from '../../design/icons.js';
import { ActionMenu } from '../action-menu.js';
import { Overlay } from '../overlay.js';
import { createLead, type CreateAction } from '../../app/create-actions.js';

function useOrigin(): string {
  const location = useLocation();
  return `${location.pathname}${location.search}`;
}

/** Feuille du téléphone : le geste principal en grand, puis une grille de cibles larges. */
export function CreateSheet(props: {
  actions: readonly CreateAction[];
  /** Vue terrain : le scanner de QR code est le geste principal. */
  terrain: boolean;
  onClose: () => void;
  onScan: () => void;
}): ReactNode {
  const from = useOrigin();
  const Scan = ICONS.qr;
  const lead = createLead(props.actions, props.terrain);
  const others = props.actions.filter((action) => action !== lead);
  const LeadIcon = lead === 'scan' ? Scan : lead.icon;
  const scan = (): void => {
    props.onClose();
    props.onScan();
  };
  return (
    <Overlay label={t('nav.creerTitre')} onClose={props.onClose} variant="feuille" swipeDismiss>
      <div className="create-sheet">
        <div className="create-sheet__head">
          <h2 className="create-sheet__title">{t('nav.creerTitre')}</h2>
          <p className="create-sheet__hint">{t('nav.creerDetail')}</p>
        </div>
        {lead === 'scan' ? (
          <button type="button" className="create-sheet__scan" onClick={scan}>
            <LeadIcon size={22} aria-hidden="true" />
            <span>{t('qr.scanner')}</span>
          </button>
        ) : (
          <Link className="create-sheet__scan" to={lead.to} state={{ from }} onClick={props.onClose}>
            <LeadIcon size={22} aria-hidden="true" />
            <span>{lead.label}</span>
          </Link>
        )}
        <ul className="create-sheet__grid">
          {others.map((action) => {
            const Icon = action.icon;
            return (
              <li key={action.to}>
                <Link className="create-sheet__item" to={action.to} state={{ from }} onClick={props.onClose}>
                  <span className="create-sheet__icon" aria-hidden="true">
                    <Icon size={22} />
                  </span>
                  <span className="create-sheet__label">{action.label}</span>
                </Link>
              </li>
            );
          })}
          {lead === 'scan' ? null : (
            <li>
              <button type="button" className="create-sheet__item create-sheet__item--button" onClick={scan}>
                <span className="create-sheet__icon" aria-hidden="true">
                  <Scan size={22} />
                </span>
                <span className="create-sheet__label">{t('qr.scanner')}</span>
              </button>
            </li>
          )}
        </ul>
      </div>
    </Overlay>
  );
}

/** Bouton de la colonne latérale : un menu sous le bouton, au clavier comme à la souris. */
export function CreateMenuButton(props: { actions: readonly CreateAction[]; terrain: boolean; onScan: () => void }): ReactNode {
  const from = useOrigin();
  const Scan = ICONS.qr;
  // Au terrain le scanner ouvre la liste, au bureau il la ferme (comme la feuille du téléphone).
  const scanItem = { label: t('qr.scanner'), icon: <Scan size={18} aria-hidden="true" />, onSelect: props.onScan };
  return (
    <ActionMenu
      label={t('nav.creer')}
      trigger={{
        className: 'side-nav-create',
        content: (
          <>
            <Plus size={20} strokeWidth={2.4} aria-hidden="true" />
            <span className="nav-label">{t('nav.creer')}</span>
          </>
        ),
      }}
      items={[
        ...(props.terrain ? [scanItem] : []),
        ...props.actions.map((action) => {
          const Icon = action.icon;
          return { label: action.label, icon: <Icon size={18} aria-hidden="true" />, to: action.to, state: { from } };
        }),
        ...(props.terrain ? [] : [scanItem]),
      ]}
    />
  );
}
